import usePreviewMode from '@/modules/core/previewMode/hooks/usePreviewMode';
import useQuickPreview from '@/modules/core/quickPreview/hooks/useQuickPreview';
import { ALERT_TYPES } from '@/modules/core/sentry/sentry';
import { Amplitude } from '@amplitude/react-amplitude';
import {
  css,
  useTheme,
} from '@emotion/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as Sentry from '@sentry/node';
import deepmerge from 'deepmerge';
import find from 'lodash.find';
import map from 'lodash.map';
import remove from 'lodash.remove';
import size from 'lodash.size';
import React, {
  ChangeEvent,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { LogEvent } from '@/modules/core/amplitude/types/Amplitude';
import { StudentSolution } from '@/modules/core/data/types/StudentSolution';
import { GenericObject } from '@/modules/core/data/types/GenericObject';
import { AMPLITUDE_ACTIONS } from '@/modules/core/amplitude/amplitude';
import Btn from '@/components/dataDisplay/Btn';

type Props = {
  studentSolution: StudentSolution;
  isReportDialogOpen: boolean;
  setIsReportDialogOpen: (isOpen: boolean) => void;
  isReportSent: boolean;
  setIsReportSent: (isSent: boolean) => void;
}

type Reason = { ref: string, label: string };

const SolutionReportDialog: React.FunctionComponent<Props> = (props): JSX.Element => {
  const {
    studentSolution,
    isReportDialogOpen,
    setIsReportDialogOpen,
    isReportSent,
    setIsReportSent,
  } = props;
  const { t } = useTranslation();
  const { surfaceColor, onSurfaceColor, primaryColor } = useTheme();
  const [selectedReasons, setSelectedReasons] = useState<Reason[]>([]);
  const [testimony, setTestimony] = useState<string>(undefined);
  const { isPreviewModeEnabled } = usePreviewMode();
  const { isQuickPreviewPage } = useQuickPreview();
  const shouldSendReportUponSubmit = !(isPreviewModeEnabled || isQuickPreviewPage);
  const reasons: Reason[] = [
    {
      ref: 'solution-not-available',
      label: t('solutionPage.solutionReportDialog.reason.solution-not-available.label', `Cette solution n'est plus disponible`),
    },
    {
      ref: 'solution-content-spelling',
      label: t('solutionPage.solutionReportDialog.reason.solution-content-spelling.label', `Faute d'orthographe dans le contenu`),
    },
    {
      ref: 'solution-content-incorrect-or-confusing',
      label: t('solutionPage.solutionReportDialog.reason.solution-content-incorrect-or-confusing.label', `Le contenu semble incorrect / porte à confusion`),
    },
    {
      ref: 'solution-not-usable',
      label: t('solutionPage.solutionReportDialog.reason.solution-not-usable.label', `Je n'ai pas pu obtenir l'aide dans les conditions annoncées (prêt, bourses, etc)`),
    },
    {
      ref: 'solution-contact-incorrect-or-unreachable',
      label: t('solutionPage.solutionReportDialog.reason.solution-contact-incorrect-or-unreachable.label', `Un contact indiqué est incorrect / est injoignable`),
    },
    {
      ref: 'solution-link-incorrect',
      label: t('solutionPage.solutionReportDialog.reason.solution-link-incorrect.label', `Un lien ne fonctionne pas`),
    },
    {
      ref: 'solution-documents-content-issue',
      label: t('solutionPage.solutionReportDialog.reason.solution-documents-content-issue.label', `Il y a un problème avec les pièces-jointes`),
    },
    {
      ref: 'solution-logo-display-issue',
      label: t('solutionPage.solutionReportDialog.reason.solution-logo-display-issue.label', `Le logo s'affiche mal`),
    },
    {
      ref: 'solution-miscategorized',
      label: t('solutionPage.solutionReportDialog.reason.solution-miscategorized.label', `Cette solution est mal catégorisée`),
    },
    {
      ref: 'solution-other',
      label: t('solutionPage.solutionReportDialog.reason.solution-other.label', `Autre`),
    },
  ];
  const isSubmitButtonDisabled: boolean = size(selectedReasons) < 1;

  /**
   * Handles changes within the "reason" checkboxes and update state accordingly (select/unselect).
   *
   * @param event
   */
  const handleOnChangeReasonCheckbox = (event: ChangeEvent<HTMLInputElement>) => {
    const isChecked: boolean = event.target.checked;
    const reasonRef: string = event.target.id;
    const selectedReason: Reason = find(reasons, { ref: reasonRef });

    if (isChecked) {
      // Copy array (avoid mutation) and add selected item
      const newSelectedReasons: Reason[] = [
        ...selectedReasons,
        selectedReason,
      ];

      setSelectedReasons(newSelectedReasons);
    } else {
      // Copy array (avoid mutation) and find/remove selected item
      const newSelectedReasons: Reason[] = [
        ...selectedReasons,
      ];
      remove(newSelectedReasons, (newSelectedReason: Reason) => newSelectedReason?.ref === selectedReason?.ref);

      setSelectedReasons(newSelectedReasons);
    }
  };

  /**
   * Submit the report.
   *
   * Send event to Sentry to allow our Customer Success team to dive into the report through a Slack alert.
   * Send event to Amplitude to allow our CS/Sales teams to build an analytic report about what solutions have been reported.
   *
   * @param logEvent
   */
  const handleOnSubmitReport = (logEvent: LogEvent) => {
    if (!shouldSendReportUponSubmit) {
      alert(t(`solutionPage.solutionReportDialog.reportDisabled.description`, `Cette fonctionnalité est désactivée dans l'environnement de prévisualisation, aucune alerte n'est réellement envoyée.`));
    } else {
      const selectedReasonsCount: number = size(selectedReasons);
      const reportCommonProps: GenericObject = {
        reportedSolution: {
          id: studentSolution?.id,
          ref: studentSolution?.ref,
          label: studentSolution?.label,
          isGenericDuplicate: studentSolution?.isGenericDuplicate,
          genericDuplicateType: studentSolution?.genericDuplicateType,
          version: studentSolution?.version,
          isExpired: studentSolution?.isExpired,
          behaviourWhenExpired: studentSolution?.behaviourWhenExpired,
        },
        report: {
          selectedReasonsCount: selectedReasonsCount,
          testimony: testimony,
        },
      };
      // Sentry cannot work with arrays, so they must all be stringified
      const sentryReportProps = deepmerge(reportCommonProps, {
        report: {
          selectedReasons: JSON.stringify(selectedReasons),
          selectedReasonsRefs: JSON.stringify(map(selectedReasons, 'ref')),
          selectedReasonsLabels: JSON.stringify(map(selectedReasons, 'label')),
        },
      });
      // Amplitude must use arrays, otherwise the data aren't exploitable
      const amplitudeReportProps = deepmerge(reportCommonProps, {
        report: {
          selectedReasons: selectedReasons,
          selectedReasonsRefs: map(selectedReasons, 'ref'),
          selectedReasonsLabels: map(selectedReasons, 'label'),
        },
      });

      // Send report to Sentry, which will in turn send a Slack notification
      Sentry.withScope((scope): void => {
        scope.setContext('report', sentryReportProps);
        scope.setTag('alertType', ALERT_TYPES.SOLUTION_REPORT);
        scope.setTag('solutionIsGenericDuplicate', `${!!studentSolution?.isGenericDuplicate}`);
        scope.setTag('solutionGenericDuplicateType', studentSolution?.genericDuplicateType);
        scope.setTag('solutionRef', studentSolution?.ref);
        scope.setTag('solutionRefHumanFriendly', `${studentSolution?.ref} (v${studentSolution?.version}) - ${studentSolution?.label}`);
        scope.setTag('selectedReasonsLabels', JSON.stringify(map(selectedReasons, 'label')));
        scope.setTag('testimony', testimony || 'No testimony given');

        Sentry.captureEvent({
          message: `The solution "${studentSolution?.label}" has been reported with ${selectedReasonsCount} reason${selectedReasonsCount > 1 ? 's' : ''}: "${testimony}"`,
          level: Sentry.Severity.Warning,
        });
      });

      // Send report to Amplitude
      logEvent(`solution-report`, {
        ...amplitudeReportProps,
        action: AMPLITUDE_ACTIONS.CLICK,
      });
    }

    // Mark report as sent
    setIsReportSent(true);

    // Reset all the inputs
    setTestimony(undefined);
    setSelectedReasons([]);
  };

  /**
   * Close the dialog and mark the report as not sent so that the next time the user opens it it'll display the dialog again.
   */
  const closeAndResetDialog = () => {
    setIsReportDialogOpen(false);
    setIsReportSent(false);
  };

  if (!isReportDialogOpen) {
    return null;
  }

  return (
    <Amplitude>
      {
        ({ logEvent }: { logEvent: LogEvent }): JSX.Element => (
          <div
            css={css`
              .backdrop {
                position: fixed;
                width: 100vw;
                height: 100vh;
                background-color: black;
                opacity: 0.4;
                top: 0;
                left: 0;
                z-index: 1;
              }

              .modal-container {
                position: fixed;
                top: 50%;
                left: 50%;
                z-index: 1;
                background-color: ${surfaceColor};
                padding: 20px;
                transform: translate(-50%, -50%);
                border-radius: 25px;
                text-align: center;
                width: 50%;

                @media (max-width: 991.98px) {
                  width: 100%;
                  max-height: 100vh;
                  overflow-x: scroll;
                }
              }

              .title {
                color: ${primaryColor};
                margin-top: 20px;
                margin-bottom: 30px;
              }

              .title > h6 {
                color: ${onSurfaceColor};
              }

              input {
                margin-right: 30px;
                color: ${onSurfaceColor};
                border: 1px solid ${primaryColor};
                border-radius: 10px;
              }

              input[type="radio"]:checked {
                color: ${primaryColor};
              }

              ul {
                list-style: none;
                margin-bottom: 30px;
                text-align: left;
                padding-left: 0;
              }

              .inputs {
                margin: 0 20px 20px 20px;

                @media (max-width: 991.98px) {
                  margin: 0px;
                }
              }

              .close {
                float: right;
              }

              .testimony {
                width: 100%;
                margin-top: 5px;
                margin-bottom: 30px;
              }
            `}
          >
            <div
              className="backdrop"
              onClick={closeAndResetDialog}
              onKeyDown={closeAndResetDialog}
              role={'button'}
              tabIndex={0}
            >
            </div>
            <div className="modal-container">
              <div className="close">
                <Btn
                  onClick={closeAndResetDialog}
                >
                  <FontAwesomeIcon icon="times" />
                </Btn>
              </div>

              {
                !isReportSent ? (
                  <div className="inputs">
                    <div className="title">
                      <h2>{t('solutionPage.solutionReportDialog.reportNotSent.title', `Quel est le problème avec cette solution ?`)}</h2>
                      <h6><b>{studentSolution?.label}</b></h6>
                    </div>

                    <ul>
                      {
                        map(reasons, (reason) => {
                          const { ref, label } = reason;

                          return (
                            <li key={ref}>
                              <input
                                type="checkbox"
                                id={ref}
                                name={ref}
                                checked={!!find(selectedReasons, { ref: ref })}
                                onChange={handleOnChangeReasonCheckbox}
                              />
                              <label
                                htmlFor={ref}
                              >
                                {label}
                              </label>
                            </li>
                          );
                        })
                      }
                    </ul>

                    <textarea
                      rows={4}
                      className="testimony"
                      placeholder={t('solutionPage.solutionReportDialog.reportNotSent.testimony.placeholder', `N'hésitez pas à nous en dire plus !`)}
                      value={testimony}
                      onChange={(event: ChangeEvent<HTMLTextAreaElement>) => setTestimony(event?.target?.value)}
                    />
                    <Btn
                      disabled={isSubmitButtonDisabled}
                      onClick={handleOnSubmitReport.bind(this, logEvent)}
                    >
                      <FontAwesomeIcon icon="paper-plane" />
                      {t('solutionPage.solutionReportDialog.reportNotSent.submit.label', `Envoyer`)}
                    </Btn>
                  </div>
                ) : (
                  <div className="title">
                    <h2>{t('solutionPage.solutionReportDialog.reportSent.title', `Merci pour votre aide.`)}</h2>
                    <h6>{t('solutionPage.solutionReportDialog.reportSent.description', `Nous avons bien reçu votre signalement et allons intervenir au plus vite.`)}</h6>
                  </div>
                )
              }
            </div>
          </div>
        )
      }
    </Amplitude>
  );
};

export default SolutionReportDialog;
